// ============================================================================
// WELOOP CHART UTILITIES
// ============================================================================
// This file contains common chart configurations and helper functions

// ============================================================================
// CHART COLOR PALETTES
// ============================================================================
export const CHART_COLORS = {
  // Primary color palette
  primary: [
    "hsl(221.2 83.2% 53.3%)", // Blue
    "hsl(217.2 91.2% 59.8%)", // Light Blue
    "hsl(214.3 31.8% 91.4%)", // Very Light Blue
    "hsl(210 40% 96%)",       // Gray Blue
  ],
  
  // Success color palette
  success: [
    "hsl(142.1 76.2% 36.3%)", // Green
    "hsl(158.1 64.4% 52.0%)", // Light Green
    "hsl(152.3 47.4% 11.8%)", // Dark Green
    "hsl(143.8 61.2% 20.2%)", // Medium Green
  ],
  
  // Warning color palette
  warning: [
    "hsl(38 92% 50%)",        // Orange
    "hsl(48 96% 89%)",        // Light Orange
    "hsl(25 95% 53%)",        // Dark Orange
    "hsl(47 95% 57%)",        // Yellow
  ],
  
  // Danger color palette
  danger: [
    "hsl(0 84.2% 60.2%)",     // Red
    "hsl(0 93.3% 94.1%)",     // Light Red
    "hsl(0 72.2% 50.6%)",     // Dark Red
    "hsl(0 74% 74.9%)",       // Medium Red
  ],
  
  // Neutral color palette
  neutral: [
    "hsl(215 20.2% 65.1%)",   // Gray
    "hsl(215 16.3% 46.9%)",   // Dark Gray
    "hsl(215 25% 27.1%)",     // Very Dark Gray
    "hsl(215 14.3% 34.1%)",   // Medium Gray
  ],
  
  // Dark theme colors
  dark: [
    "#60a5fa",                // Blue
    "#34d399",                // Green
    "#fbbf24",                // Yellow
    "#f87171",                // Red
  ],
  
  // Minimal theme colors
  minimal: [
    "#6b7280",                // Gray
    "#9ca3af",                // Light Gray
    "#d1d5db",                // Very Light Gray
    "#e5e7eb",                // Extra Light Gray
  ],
} as const;

// ============================================================================
// CHART THEMES
// ============================================================================
export const CHART_THEMES = {
  light: {
    colors: CHART_COLORS.primary,
    backgroundColor: "#ffffff",
    textColor: "#000000",
    gridColor: "rgba(0,0,0,0.1)",
    axisColor: "#888888",
  },
  dark: {
    colors: CHART_COLORS.dark,
    backgroundColor: "#1f2937",
    textColor: "#ffffff",
    gridColor: "rgba(255,255,255,0.1)",
    axisColor: "#9ca3af",
  },
  minimal: {
    colors: CHART_COLORS.minimal,
    backgroundColor: "#ffffff",
    textColor: "#6b7280",
    gridColor: "rgba(107,114,128,0.1)",
    axisColor: "#9ca3af",
  },
} as const;

// ============================================================================
// CHART CONFIGURATIONS
// ============================================================================
export const CHART_CONFIGS = {
  // Common chart configurations
  common: {
    responsive: true,
    accessible: true,
    showTooltip: true,
    showLegend: false,
    showGrid: true,
  },
  
  // Area chart specific configurations
  area: {
    gradient: true,
    fillOpacity: 0.3,
    strokeWidth: 2,
    type: "monotone" as const,
  },
  
  // Bar chart specific configurations
  bar: {
    gradient: true,
    strokeWidth: 2,
    radius: 4,
    horizontal: false,
    stacked: false,
    grouped: false,
  },
  
  // Line chart specific configurations
  line: {
    strokeWidth: 2,
    showDots: true,
    showActiveDot: true,
    dotRadius: 4,
    activeDotRadius: 6,
    type: "monotone" as const,
  },
  
  // Pie chart specific configurations
  pie: {
    innerRadius: 0,
    outerRadius: "80%",
    showLabels: true,
    paddingAngle: 0,
    startAngle: 0,
    endAngle: 360,
  },
  
  // Radar chart specific configurations
  radar: {
    strokeWidth: 2,
    fillOpacity: 0.3,
    showDots: false,
    showActiveDot: false,
    dotRadius: 4,
    activeDotRadius: 6,
    gridType: "polygon" as const,
    domain: [0, 150] as [number, number],
  },
  
  // Radial chart specific configurations
  radial: {
    strokeWidth: 2,
    showLabels: false,
    innerRadius: "20%",
    outerRadius: "80%",
    startAngle: 0,
    endAngle: 360,
    cornerRadius: 4,
    background: false,
  },
} as const;

// ============================================================================
// CHART VARIANTS
// ============================================================================
export const CHART_VARIANTS = {
  area: ["default", "animated", "interactive", "minimal", "gradient", "stacked", "curved", "stepped"] as const,
  bar: ["default", "animated", "interactive", "minimal", "gradient", "stacked", "grouped"] as const,
  line: ["default", "animated", "interactive", "minimal", "curved", "stepped", "dashed"] as const,
  pie: ["default", "animated", "interactive", "minimal", "donut", "gradient"] as const,
  radar: ["default", "animated", "interactive", "minimal", "dots", "lines", "filled"] as const,
  radial: ["default", "animated", "interactive", "minimal", "stacked", "text", "shape"] as const,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get colors for a specific theme
 */
export function getThemeColors(theme: keyof typeof CHART_THEMES = "light") {
  return CHART_THEMES[theme].colors;
}

/**
 * Get chart configuration for a specific chart type
 */
export function getChartConfig(chartType: keyof typeof CHART_CONFIGS) {
  return {
    ...CHART_CONFIGS.common,
    ...CHART_CONFIGS[chartType],
  };
}

/**
 * Create a chart configuration object for tooltips and legends
 */
export function createChartConfig(
  dataKeys: string[],
  labels?: Record<string, string>,
  colors?: string[]
) {
  const config: Record<string, any> = {};
  const themeColors = colors || CHART_COLORS.primary;
  
  dataKeys.forEach((key, index) => {
    config[key] = {
      label: labels?.[key] || key,
      color: themeColors[index % themeColors.length],
    };
  });
  
  return config;
}

/**
 * Format number for display in charts
 */
export function formatChartValue(value: number, format: "number" | "currency" | "percentage" = "number") {
  switch (format) {
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    case "percentage":
      return `${value.toFixed(1)}%`;
    default:
      return value.toLocaleString();
  }
}

/**
 * Generate sample data for charts
 */
export function generateSampleData(
  type: "area" | "bar" | "line" | "pie" | "radar" | "radial",
  count: number = 6
) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
  const subjects = ["Math", "Science", "English", "History", "Geography", "Art"];
  
  switch (type) {
    case "area":
    case "bar":
    case "line":
      return months.slice(0, count).map((month) => ({
        name: month,
        total: Math.floor(Math.random() * 5000) + 1000,
        sales: Math.floor(Math.random() * 3000) + 500,
        revenue: Math.floor(Math.random() * 2000) + 300,
        users: Math.floor(Math.random() * 4000) + 800,
      }));
    
    case "pie":
      return [
        { name: "Sales", value: 45 },
        { name: "Marketing", value: 25 },
        { name: "Development", value: 20 },
        { name: "Support", value: 10 },
      ];
    
    case "radar":
      return subjects.slice(0, count).map((subject) => ({
        subject,
        A: Math.floor(Math.random() * 150) + 50,
        B: Math.floor(Math.random() * 150) + 50,
        fullMark: 150,
      }));
    
    case "radial":
      return [
        { name: "18-24", value: 31.47 },
        { name: "25-29", value: 26.69 },
        { name: "30-34", value: 15.69 },
        { name: "35-39", value: 8.22 },
      ];
    
    default:
      return [];
  }
}

/**
 * Get chart variants for a specific chart type
 */
export function getChartVariants<T extends keyof typeof CHART_VARIANTS>(chartType: T) {
  return CHART_VARIANTS[chartType];
}

/**
 * Validate chart data
 */
export function validateChartData(data: any[], requiredKeys: string[] = ["name", "value"]) {
  if (!Array.isArray(data) || data.length === 0) {
    return false;
  }
  
  return data.every((item) => {
    return requiredKeys.every((key) => key in item);
  });
}

/**
 * Calculate chart dimensions based on container
 */
export function calculateChartDimensions(
  containerWidth: number,
  containerHeight: number,
  aspectRatio: number = 16 / 9
) {
  const maxWidth = containerWidth;
  const maxHeight = containerHeight;
  
  let width = maxWidth;
  let height = width / aspectRatio;
  
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  
  return { width, height };
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================
export type ChartTheme = keyof typeof CHART_THEMES;
export type ChartType = keyof typeof CHART_CONFIGS;
export type ChartVariant<T extends keyof typeof CHART_VARIANTS> = typeof CHART_VARIANTS[T][number];
export type ChartColorPalette = keyof typeof CHART_COLORS; 